import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import heroVideo from "@/assets/hero-family-home.mp4";

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-canvas lg:min-h-[85vh] lg:flex lg:items-center">
      {/* Background hero video — desktop: cover absolute; mobile/tablet: below copy */}
      <div className="hidden lg:block lg:absolute lg:inset-0 lg:-z-0 lg:pointer-events-none w-full bg-canvas">
        <video
          src={heroVideo}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
        {/* Ambient warmth — desktop only */}
        <div className="hidden lg:block absolute -top-40 -right-32 w-[600px] h-[600px] rounded-full bg-gold/10 blur-3xl animate-float-slow" />
        <div className="hidden lg:block absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-wood/10 blur-3xl animate-float-fast" />
      </div>

      <div className="relative lg:z-10 w-full max-w-[960px] mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-12 lg:pt-40 lg:pb-16">
        <div className="grid grid-cols-1 gap-8 lg:gap-12 items-center">
          {/* Mobile/tablet video — above copy */}
          <div className="lg:hidden -mx-6 sm:-mx-8 order-first">
            <video
              src={heroVideo}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
              className="w-full h-auto object-contain"
            />
          </div>

          {/* Editorial copy — below video on mobile/tablet, overlay on desktop */}
          <div className="reveal-up max-w-2xl w-full">
            <div className="inline-flex items-center gap-2.5 mb-5">
              <span className="w-8 h-px bg-gold" />
              <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-ink lg:text-white lg:drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
                No Real Estate Agent · Central Florida
              </span>
            </div>

            <h1 className="font-display text-[44px] sm:text-6xl lg:text-7xl font-semibold text-ink lg:text-white leading-[1.0] tracking-tight mb-5 lg:[text-shadow:0_4px_28px_rgba(0,0,0,0.85),0_2px_8px_rgba(0,0,0,0.6)]">
              <span>The </span>
              <span className="accent-italic text-gold">AI real estate agent</span>
              <br />
              <span>for </span>
              <span className="accent-italic text-gold">smarter, faster</span>
              <br />
              <span>home sales.</span>
            </h1>

            <p className="text-base lg:text-lg text-ink-soft lg:text-white leading-[1.65] max-w-[540px] mb-7 font-medium lg:[text-shadow:0_2px_14px_rgba(0,0,0,0.8)]">
              REALV3 is the no real estate agent platform for Central Florida buyers, sellers, and builders — guided end-to-end by your AI Agent. List, tour, offer, and close without paying a 3–6% commission.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <Link to="/list-property">
                <Button
                  size="lg"
                  className="group bg-gold hover:bg-gold-deep text-ink font-medium px-7 py-6 text-sm tracking-wide rounded-md shadow-soft hover:shadow-lift transition-all duration-500"
                >
                  List my home
                  <ArrowUpRight className="ml-2 w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
              </Link>
              <Link to="/listings">
                <Button
                  size="lg"
                  variant="outline"
                  className="border border-white/40 lg:border-white/60 bg-transparent hover:bg-white hover:text-ink hover:border-white text-ink lg:text-white font-medium px-7 py-6 text-sm tracking-wide rounded-md transition-all duration-500 lg:[text-shadow:0_2px_10px_rgba(0,0,0,0.6)]"
                >
                  Browse the catalog
                </Button>
              </Link>
            </div>

            {/* Editorial stats — hairline divided */}
            <div className="grid grid-cols-3 gap-6 max-w-md pt-6 border-t border-ink/15 lg:border-white/30">
              {[
                { num: "$22k+", label: "average saved" },
                { num: "100%", label: "guided" },
                { num: "0%", label: "commission" },
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  className="reveal-up"
                  style={{ animationDelay: `${0.4 + i * 0.12}s` }}
                >
                  <div className="font-display text-3xl lg:text-4xl font-semibold text-ink lg:text-white tabular-nums leading-none mb-1.5 lg:[text-shadow:0_2px_14px_rgba(0,0,0,0.8)]">
                    {stat.num}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-ink-soft lg:text-white font-semibold lg:[text-shadow:0_2px_10px_rgba(0,0,0,0.8)]">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
